// =============================================================================
// DAMS CSV Parser — handles the real 9-section stacked inventory_report CSV
// Sections: COMPUTERS, COMPUTER SOFTWARE, PRINTERS, MOBILE DEVICES,
//           OTHER EQUIPMENT, NETWORK DEVICES, SYSTEMS,
//           NETWORK SURVEY, SECURITY SURVEY
// =============================================================================

import type { ParsedInventory } from './types';

// ---------------------------------------------------------------------------
// Section keyword registry (order matters — longer matches first)
// ---------------------------------------------------------------------------
const SECTION_KEYS = [
  'COMPUTER SOFTWARE',
  'COMPUTERS',
  'PRINTERS',
  'MOBILE DEVICES',
  'OTHER EQUIPMENT',
  'NETWORK DEVICES',
  'SYSTEMS',
  'NETWORK SURVEY',
  'SECURITY SURVEY',
] as const;
type SectionKey = typeof SECTION_KEYS[number];

// ---------------------------------------------------------------------------
// Low-level CSV line parser (handles quoted fields with commas/newlines)
// ---------------------------------------------------------------------------
function parseLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === ',' && !inQ) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function stripQuotes(s: string): string {
  return s.replace(/^"|"$/g, '').trim();
}

function isBlank(line: string): boolean {
  return line.replace(/,/g, '').trim() === '';
}

// Returns the section keyword if this line is a section header, else null
function detectSection(line: string): SectionKey | null {
  const clean = stripQuotes(line).toUpperCase().trim();
  for (const kw of SECTION_KEYS) {
    if (clean === kw) return kw;
  }
  return null;
}

function isStatistics(line: string): boolean {
  return stripQuotes(line).toUpperCase().trim() === 'STATISTICS';
}

// Lines that look like metadata before actual CSV headers
function isMetaLine(line: string): boolean {
  const l = line.trim();
  return (
    l.startsWith('Warning:') ||
    l.startsWith('Notice:') ||
    l.startsWith('<b>') ||
    l.startsWith('<br') ||
    l.startsWith('<?') ||
    l.startsWith('Generated:') ||
    l.startsWith('"Generated:') ||
    l.startsWith('Department Name:') ||
    l.startsWith('"Department Name:') ||
    l.startsWith('Office:') ||
    l.startsWith('Location:') ||
    l.startsWith('Survey ID:') ||
    l.startsWith('"Survey ID:') ||
    l.startsWith('"FULL DATABASE') ||
    l.startsWith('FULL DATABASE')
  );
}

// Detect if a line is a CSV header row (has recognisable column names)
function looksLikeHeader(line: string): boolean {
  const cols = parseLine(line).map(s => s.toLowerCase());
  return (
    cols.includes('computer_id') ||
    cols.includes('printer_id')  ||
    cols.includes('mobile_id')   ||
    cols.includes('equipment_id')||
    cols.includes('device_id')   ||
    cols.includes('system_id')   ||
    cols.includes('id')          ||
    cols.includes('question')
  );
}

// ---------------------------------------------------------------------------
// Row → object using headers
// ---------------------------------------------------------------------------
function toObj(headers: string[], row: string[]): Record<string, string> {
  const obj: Record<string, string> = {};
  headers.forEach((h, i) => { obj[h] = (row[i] ?? '').trim(); });
  return obj;
}

// ---------------------------------------------------------------------------
// Parse one section's lines into rows of objects
// ---------------------------------------------------------------------------
function parseSection(lines: string[]): Record<string, string>[] {
  // Find the first line that looks like a header
  let headerIdx = lines.findIndex(looksLikeHeader);
  if (headerIdx === -1) {
    // Fallback: first non-blank, non-meta line
    headerIdx = lines.findIndex(l => !isBlank(l) && !isMetaLine(l));
  }
  if (headerIdx === -1) return [];

  const headers = parseLine(lines[headerIdx]).map(stripQuotes);
  const rows: Record<string, string>[] = [];

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (isBlank(line) || isStatistics(line)) break;
    const cols = parseLine(line);
    if (cols.length < 1 || cols.every(c => c === '')) continue;
    rows.push(toObj(headers, cols));
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------
export function parseInventoryCSV(rawText: string): ParsedInventory {
  const parseErrors: string[] = [];

  // Normalise line endings
  const allLines = rawText.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  // Bucket lines into sections
  const buckets: Partial<Record<SectionKey, string[]>> = {};
  let current: SectionKey | null = null;
  let inStatistics = false;

  for (const line of allLines) {
    // Skip PHP warnings and HTML from top
    if (isMetaLine(line)) { current = null; inStatistics = false; continue; }

    const sec = detectSection(line);
    if (sec) {
      current = sec;
      inStatistics = false;
      if (!buckets[sec]) buckets[sec] = [];
      continue;
    }

    if (isStatistics(line)) { inStatistics = true; continue; }

    // While inside a STATISTICS block, skip until next section header or blank streak
    if (inStatistics) continue;

    if (current && !isBlank(line)) {
      buckets[current]!.push(line);
    }
  }

  // Parse each section
  function safe(key: SectionKey): Record<string, string>[] {
    try {
      return parseSection(buckets[key] ?? []);
    } catch (e) {
      parseErrors.push(`${key} parse error: ${String(e)}`);
      return [];
    }
  }

  const computers        = safe('COMPUTERS');
  const computerSoftware = safe('COMPUTER SOFTWARE');
  const printers         = safe('PRINTERS');
  const mobileDevices    = safe('MOBILE DEVICES');
  const otherEquipment   = safe('OTHER EQUIPMENT');
  const networkDevices   = safe('NETWORK DEVICES');
  const systems          = safe('SYSTEMS');
  const networkSurvey    = safe('NETWORK SURVEY');
  const securitySurvey   = safe('SECURITY SURVEY');

  return {
    computers, computerSoftware, printers, mobileDevices,
    otherEquipment, networkDevices, systems, networkSurvey,
    securitySurvey, parseErrors,
  };
}

// ---------------------------------------------------------------------------
// Normalisers — raw CSV objects → typed domain models
// ---------------------------------------------------------------------------
import type {
  Computer, ComputerSoftware, Printer, MobileDevice,
  OtherEquipment, NetworkDevice, CitySystem,
  NetworkSurveyItem, SecuritySurveyItem,
} from './types';
import { CURRENT_YEAR, EOL_THRESHOLD_YEARS, DEPARTMENT_MAP } from './isspAnalytics';

function normalizeYear(s: string): number {
  const n = parseInt(s, 10);
  return n >= 1990 && n <= 2099 ? n : 0;
}

// Clamp to known departments (any dept_id present in DEPARTMENT_MAP). Unknown codes → 999.
function safeDeptId(raw: string): number {
  const n = parseInt(raw, 10);
  if (isNaN(n)) return 999;
  return DEPARTMENT_MAP[n] ? n : 999;
}

function normalizeStatus(s: string): string {
  const l = s.toLowerCase().trim();
  if (l === 'active')       return 'Active';
  if (l === 'inactive')     return 'Inactive';
  if (l.includes('defect')) return 'Defective';
  if (l.includes('dispos')) return 'For Disposal';
  if (l.includes('repair')) return 'For Repair';
  return s || 'Active';
}

function normalizeUtilization(s: string): string {
  const l = s.toLowerCase().trim();
  if (l.includes('admin'))    return 'Administrative';
  if (l.includes('frontline'))return 'Frontline';
  return s || 'Administrative';
}

// Windows 11 is the only current Windows release; Win10 and below are EOL.
// Non-Windows falls back to a year-based threshold.
function isEolByOs(os: string, year: number): boolean {
  const l = os.toLowerCase().replace(/[\s\-_.]/g, '');
  if (l.startsWith('win') || l.includes('windows')) {
    return !l.includes('11');
  }
  return year > 0 && (CURRENT_YEAR - year) >= EOL_THRESHOLD_YEARS;
}

export function normalizeComputers(raw: Record<string, string>[]): Computer[] {
  return raw.map(r => {
    const yr = normalizeYear(r['year_acquired'] ?? '');
    return {
      computer_id:   r['computer_id']  || `COMP-${Math.random().toString(36).slice(2,8).toUpperCase()}`,
      brand_serial:  r['brand_serial'] || '',
      type:          r['type']         || 'Other',
      year_acquired: yr,
      utilization:   normalizeUtilization(r['utilization'] ?? ''),
      os:            r['os']           || '',
      os_license:    r['os_license']   || 'Unknown',
      status:        normalizeStatus(r['status'] ?? ''),
      remarks:       r['remarks']      || '',
      dept_id:       safeDeptId(r['dept_id'] ?? ''),
      eol_flag:      isEolByOs(r['os'] ?? '', yr),
    };
  });
}

export function normalizeComputerSoftware(raw: Record<string, string>[], computers: Computer[]): ComputerSoftware[] {
  // Build dept lookup from computers
  const deptByComputer: Record<string, number> = {};
  computers.forEach(c => { deptByComputer[c.computer_id] = c.dept_id; });

  return raw.map((r, i) => {
    const compId = r['computer_id'] || r['computer_serial'] || '';
    return {
      id:               parseInt(r['id'] ?? '0', 10) || i + 1000,
      brand_serial:     r['brand_serial']     || '',
      office_software:  r['office_software']  || '',
      software_license: r['software_license'] || 'Unknown',
      remarks:          r['remarks']          || '',
      computer_id:      (compId || null) as string,  // null bypasses D1 FK check for unmatched serials
      computer_serial:  r['computer_serial']  || '',
      dept_id:          deptByComputer[compId] ?? 999,
    };
  });
}

export function normalizePrinters(raw: Record<string, string>[]): Printer[] {
  return raw.map(r => {
    const yr = normalizeYear(r['year_acquired'] ?? '');
    return {
      printer_id:    r['printer_id']  || `PRT-${Math.random().toString(36).slice(2,8).toUpperCase()}`,
      brand_serial:  r['brand_serial'] || '',
      type:          r['type']         || 'Printer',
      year_acquired: yr,
      utilization:   normalizeUtilization(r['utilization'] ?? ''),
      status:        normalizeStatus(r['status'] ?? ''),
      remarks:       r['remarks']      || '',
      dept_id:       safeDeptId(r['dept_id'] ?? ''),
      eol_flag:      isEolByOs('', yr),
    };
  });
}

export function normalizeMobileDevices(raw: Record<string, string>[]): MobileDevice[] {
  return raw.map(r => {
    const yr = normalizeYear(r['year_acquired'] ?? '');
    return {
      mobile_id:     r['mobile_id']    || `MOB-${Math.random().toString(36).slice(2,8).toUpperCase()}`,
      brand_serial:  r['brand_serial'] || '',
      type:          r['type']         || 'Smartphone',
      year_acquired: yr,
      utilization:   normalizeUtilization(r['utilization'] ?? ''),
      status:        normalizeStatus(r['status'] ?? ''),
      remarks:       r['remarks']      || '',
      dept_id:       safeDeptId(r['dept_id'] ?? ''),
      eol_flag:      isEolByOs('', yr),
    };
  });
}

export function normalizeOtherEquipment(raw: Record<string, string>[]): OtherEquipment[] {
  return raw.map(r => {
    const yr = normalizeYear(r['year_acquired'] ?? '');
    return {
      equipment_id:  r['equipment_id'] || `EQP-${Math.random().toString(36).slice(2,8).toUpperCase()}`,
      brand_serial:  r['brand_serial'] || '',
      type:          r['type']         || 'Equipment',
      year_acquired: yr,
      utilization:   normalizeUtilization(r['utilization'] ?? ''),
      status:        normalizeStatus(r['status'] ?? ''),
      remarks:       r['remarks']      || '',
      dept_id:       safeDeptId(r['dept_id'] ?? ''),
      eol_flag:      isEolByOs('', yr),
    };
  });
}

export function normalizeNetworkDevices(raw: Record<string, string>[]): NetworkDevice[] {
  const typeMap: Record<string, string> = { '1': 'Router', '2': 'Switch', '3': 'Access Point', '4': 'Firewall' };
  return raw.map(r => {
    const yr = normalizeYear(r['year_acquired'] ?? '');
    const typeCode = r['type'] || '';
    return {
      device_id:     r['device_id']    || `NET-${Math.random().toString(36).slice(2,8).toUpperCase()}`,
      brand_serial:  r['brand_serial'] || '',
      type:          typeCode,
      type_text:     r['type_text'] || typeMap[typeCode] || typeCode,
      year_acquired: yr,
      utilization:   normalizeUtilization(r['utilization'] ?? ''),
      status:        normalizeStatus(r['status'] ?? ''),
      remarks:       r['remarks']      || '',
      dept_id:       safeDeptId(r['dept_id'] ?? ''),
    };
  });
}

export function normalizeSystems(raw: Record<string, string>[]): CitySystem[] {
  return raw.map(r => ({
    system_id:            r['system_id']            || `SYS-${Math.random().toString(36).slice(2,8).toUpperCase()}`,
    system_name:          r['system_name']           || '',
    developer:            r['developer']             || '',
    developer_details:    r['developer_details']     || '',
    development_platform: r['development_platform']  || '',
    working_development:  r['working_development']   || '',
    utilization:          normalizeUtilization(r['utilization'] ?? ''),
    maintenance_cost:     parseFloat(r['maintenance_cost']?.replace(/[^0-9.]/g, '') || '0') || 0,
    dept_id:              safeDeptId(r['dept_id'] ?? ''),
  }));
}

function categorizeNetworkQ(q: string): string {
  const l = q.toLowerCase();
  if (l.includes('lan') || l.includes('intranet'))                return 'LAN/Intranet';
  if (l.includes('wan') || l.includes('vpn'))                     return 'WAN/VPN';
  if (l.includes('internet') || l.includes('bandwidth') || l.includes('isp') || l.includes('provider')) return 'Internet';
  if (l.includes('pabx') || l.includes('pbx') || l.includes('phone')) return 'Telephony';
  if (l.includes('email') || l.includes('website'))               return 'Web/Email';
  if (l.includes('govnet') || l.includes('gov.ph'))               return 'GovNet';
  return 'General';
}

function categorizeSecurityQ(q: string): string {
  const l = q.toLowerCase();
  if (l.includes('backup') || l.includes('ups') || l.includes('generator')) return 'Power & Backup';
  if (l.includes('firewall') || l.includes('antivirus'))                     return 'Network Security';
  if (l.includes('cctv') || l.includes('physical') || l.includes('access'))  return 'Physical Security';
  if (l.includes('policy') || l.includes('issp'))                            return 'Governance';
  return 'General';
}

export function normalizeNetworkSurvey(raw: Record<string, string>[]): NetworkSurveyItem[] {
  return raw.map((r, i) => ({
    id:       i + 1,
    question: r['Question'] || r['question'] || '',
    answer:   r['Answer']   || r['answer']   || '',
    units:    r['Units']    || r['units']    || '',
    remarks:  r['Remarks']  || r['remarks']  || '',
    category: categorizeNetworkQ(r['Question'] || r['question'] || ''),
  }));
}

export function normalizeSecuritySurvey(raw: Record<string, string>[]): SecuritySurveyItem[] {
  return raw.map((r, i) => ({
    id:       i + 1,
    question: r['Question'] || r['question'] || '',
    answer:   r['Answer']   || r['answer']   || '',
    details:  r['Details']  || r['details']  || '',
    category: categorizeSecurityQ(r['Question'] || r['question'] || ''),
  }));
}
